/**
 * Centralised dynamic-import factories for every lazy-loaded route, plus
 * helpers to warm the browser's module cache before the user actually
 * navigates. The same factories are passed to React.lazy() in App.tsx so
 * there's a single source of truth for chunk identity — calling
 * `prefetchByHref("/billing")` and `<Link href="/billing">` cause the
 * exact same chunk to be fetched, which means a hover-prefetch turns the
 * subsequent click into an instant transition with no Suspense flash.
 */

import { prefetchPlanContentListPostsFirstPage } from "@workspace/api-client-react";

export const routeImporters = {
  Login: () => import("@/pages/login"),
  Register: () => import("@/pages/register"),
  Waitlist: () => import("@/pages/waitlist"),
  MagicLinkVerify: () => import("@/pages/magic-link-verify"),
  VerifyEmail: () => import("@/pages/verify-email"),
  Checkout: () => import("@/pages/checkout"),
  PaymentFailed: () => import("@/pages/payment-failed"),
  Welcome: () => import("@/pages/welcome"),
  ForgotPassword: () => import("@/pages/forgot-password"),
  ResetPassword: () => import("@/pages/reset-password"),
  Landing: () => import("@/pages/landing"),
  Dashboard: () => import("@/pages/Dashboard"),
  Products: () => import("@/pages/products"),
  NotFound: () => import("@/pages/not-found"),
  PhotoshootHistory: () => import("@/pages/photoshoot-history"),
  Director: () => import("@/pages/director"),
  FinalImages: () => import("@/pages/final-images"),
  Admin: () => import("@/pages/admin"),
  AssetLibrary: () => import("@/pages/asset-library"),
  PhotoDirectorLibrary: () => import("@/pages/photo-director-library"),
  Upsampler: () => import("@/pages/upsampler"),
  EditImage: () => import("@/pages/edit-image"),
  Billing: () => import("@/pages/billing"),
  ModelManagement: () => import("@/pages/ModelManagement"),
  InfluencerManagement: () => import("@/pages/InfluencerManagement"),
  InfluencerRoster: () => import("@/pages/InfluencerRoster"),
  InfluencerProfile: () => import("@/pages/InfluencerProfile"),
  Profile: () => import("@/pages/profile"),
  ReportedBugs: () => import("@/pages/reported-bugs"),
  ComingSoon: () => import("@/pages/coming-soon"),
  About: () => import("@/pages/about"),
  Contact: () => import("@/pages/contact"),
  Privacy: () => import("@/pages/privacy"),
  Terms: () => import("@/pages/terms"),
} as const;

export type RouteName = keyof typeof routeImporters;

const inflight = new Set<RouteName>();

function prefetch(name: RouteName): void {
  if (inflight.has(name)) return;
  inflight.add(name);
  // Fire and forget — chunks resolve into the browser module cache so
  // the subsequent React.lazy() Suspense never has to actually wait.
  // Drop the inflight tag on failure so a future hover can retry.
  // We intentionally don't trigger chunk-recovery from a hover prefetch:
  // the user hasn't navigated yet, so the failure isn't user-visible.
  // The retry on actual navigation is handled by `lazyWithChunkRecovery`
  // in `App.tsx`.
  routeImporters[name]().catch(() => {
    inflight.delete(name);
  });
}

/**
 * Map a sidebar/nav href to the chunk(s) that route renders. Called from
 * onMouseEnter / onFocus / onTouchStart on each <Link> so the chunk is
 * already in cache by the time the click event fires.
 */
export function prefetchByHref(href: string): void {
  if (!href) return;
  if (href === "/") {
    prefetch("Dashboard");
    return;
  }
  if (href === "/dashboard") {
    prefetch("Dashboard");
    return;
  }
  if (href.startsWith("/products/")) {
    prefetch("PhotoshootHistory");
    return;
  }
  if (href.startsWith("/photoshoots/")) {
    if (href.endsWith("/final-images")) prefetch("FinalImages");
    else prefetch("Director");
    return;
  }
  if (href === "/assets") return prefetch("AssetLibrary");
  if (href === "/photo-director") return prefetch("PhotoDirectorLibrary");
  if (href === "/upsampler") return prefetch("Upsampler");
  if (href === "/edit-image") return prefetch("EditImage");
  if (href === "/model-management") return prefetch("ModelManagement");
  if (href.startsWith("/influencer-management/organisations/")) {
    return prefetch("InfluencerRoster");
  }
  if (href.startsWith("/influencer-management/influencers/")) {
    prefetch("InfluencerProfile");
    // Task #651: kick the Create Content first-page request off in parallel
    // with the route chunk download. Most users land on this tab from the
    // sidebar / a deep link with `?tab=plan-content`, and even when they
    // don't, switching to the tab afterwards adopts the same in-flight
    // promise. We extract the influencer id from the href — the route
    // pattern is `/influencer-management/influencers/:influencerId`
    // optionally followed by a query string we ignore here.
    const match = href.match(
      /^\/influencer-management\/influencers\/(\d+)(?:[/?#]|$)/,
    );
    if (match) {
      const influencerId = Number(match[1]);
      if (Number.isFinite(influencerId) && influencerId > 0) {
        // Prefetch BOTH columns — the Create Content tab renders draft
        // and approved side-by-side, so both need first-paint data.
        prefetchPlanContentListPostsFirstPage(influencerId, { column: "draft" });
        prefetchPlanContentListPostsFirstPage(influencerId, { column: "approved" });
      }
    }
    return;
  }
  if (href.startsWith("/influencer-management")) {
    return prefetch("InfluencerManagement");
  }
  if (href === "/billing") return prefetch("Billing");
  if (href === "/profile") return prefetch("Profile");
  if (href === "/admin") return prefetch("Admin");
  if (href === "/reported-bugs") return prefetch("ReportedBugs");
}

/**
 * Routes warmed in the background once the authed shell mounts. Keeps the
 * very first sidebar click instant even without a hover (e.g. touch users
 * tapping straight through, or keyboard users on first paint). Limited to
 * the most common destinations to avoid flooding the network on slow
 * connections — the rest still benefit from hover-prefetch.
 */
const COMMON_ROUTES: RouteName[] = [
  "Products",
  "AssetLibrary",
  "PhotoDirectorLibrary",
  "EditImage",
  "Billing",
  "Profile",
];

export function prefetchCommonRoutesOnIdle(): void {
  if (typeof window === "undefined") return;
  const run = () => COMMON_ROUTES.forEach(prefetch);
  const ric = (
    window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    }
  ).requestIdleCallback;
  if (typeof ric === "function") {
    ric(run, { timeout: 2000 });
  } else {
    window.setTimeout(run, 800);
  }
}
